import {
  AlignmentEnumAlignment,
  ImageFit,
  ImagePosition,
  type MediaItem,
  type MediaSettings,
} from '@wix/ambassador-forms-v2-form-template/types';

type V1QuestionImagePlacement = 'top' | 'bottom';
type V1QuestionImageFit = 'cover' | 'contain';
type V1QuestionImageAlignment = 'left' | 'center' | 'right';

type QuestionImage = {
  id?: string; // wix media id
  width?: number;
  height?: number;
  altText?: string;
};

type V1QuestionImageView = {
  questionImage?: QuestionImage;
  questionImageAltText?: string;
  questionImagePlacement?: V1QuestionImagePlacement;
  questionImageResize?: V1QuestionImageFit;
  questionImageAlignment?: V1QuestionImageAlignment;
};

type V2LabelMediaProperties = {
  media?: MediaItem;
  mediaSettings?: MediaSettings;
};

type V1QuestionImageProperties = {
  questionImage?: Omit<QuestionImage, 'altText'>;
  questionImageAltText?: string;
  questionImagePlacement?: V1QuestionImagePlacement;
  questionImageResize?: V1QuestionImageFit;
  questionImageAlignment?: V1QuestionImageAlignment;
};

export function toV2LabelMediaProperties(
  view: V1QuestionImageView,
): V2LabelMediaProperties {
  const media = toV2LabelMedia(view);
  const mediaSettings = toV2LabelMediaSettings(view);

  return {
    ...(media && { media }),
    ...(mediaSettings && { mediaSettings }),
  };
}

export function toV1QuestionImageProperties(
  media?: MediaItem,
  mediaSettings?: MediaSettings,
): V1QuestionImageProperties {
  const image = media?.image;

  if (!image) {
    return {};
  }

  const { altText, ...questionImage } = image;

  return {
    questionImage,
    ...(altText != null && { questionImageAltText: altText }),
    ...toV1QuestionImageSettings(mediaSettings),
  };
}

function toV2LabelMedia(view: V1QuestionImageView): MediaItem | undefined {
  if (!view.questionImage) {
    return undefined;
  }

  return {
    image: {
      ...view.questionImage,
      ...(view.questionImageAltText !== undefined && {
        altText: view.questionImageAltText,
      }),
    },
  };
}

function toV2LabelMediaSettings(
  view: V1QuestionImageView,
): MediaSettings | undefined {
  if (
    !view.questionImagePlacement &&
    !view.questionImageResize &&
    !view.questionImageAlignment
  ) {
    return undefined;
  }

  return {
    ...(view.questionImagePlacement && {
      imagePosition: toV2LabelImagePosition(view.questionImagePlacement),
    }),
    ...(view.questionImageResize && {
      imageFit: toV2LabelImageFit(view.questionImageResize),
    }),
    ...(view.questionImageAlignment && {
      imageAlignment: toV2LabelImageAlignment(view.questionImageAlignment),
    }),
  };
}

function toV1QuestionImageSettings(
  mediaSettings?: MediaSettings,
): V1QuestionImageProperties {
  return {
    ...(mediaSettings?.imagePosition && {
      questionImagePlacement:
        mediaSettings.imagePosition === ImagePosition.BELOW ? 'bottom' : 'top',
    }),
    ...(mediaSettings?.imageFit && {
      questionImageResize:
        mediaSettings.imageFit === ImageFit.CONTAIN ? 'contain' : 'cover',
    }),
    ...(mediaSettings?.imageAlignment && {
      questionImageAlignment:
        mediaSettings.imageAlignment === AlignmentEnumAlignment.LEFT
          ? 'left'
          : mediaSettings.imageAlignment === AlignmentEnumAlignment.RIGHT
          ? 'right'
          : 'center',
    }),
  };
}

function toV2LabelImagePosition(
  imagePlacement: V1QuestionImagePlacement,
): ImagePosition {
  return imagePlacement === 'bottom'
    ? ImagePosition.BELOW
    : ImagePosition.ABOVE;
}

function toV2LabelImageFit(imageFit: V1QuestionImageFit): ImageFit {
  return imageFit === 'contain' ? ImageFit.CONTAIN : ImageFit.COVER;
}

function toV2LabelImageAlignment(
  imageAlignment: V1QuestionImageAlignment,
): AlignmentEnumAlignment {
  if (imageAlignment === 'left') {
    return AlignmentEnumAlignment.LEFT;
  }

  if (imageAlignment === 'right') {
    return AlignmentEnumAlignment.RIGHT;
  }

  return AlignmentEnumAlignment.CENTER;
}
